/**
 * @file Job service — CRUD operations on both pipeline and jobs_pipeline stores
 */

import { store, load } from '@state/store.js';

const SALES_STAGES = ['New', 'Contacted', 'Quoted', 'Proposal Sent', 'Booked', 'Proposal Rejected'];
const JOBS_STAGES = ['Accepted', 'Scheduled', 'In Progress', 'Completed', 'Paid & Closed'];

/**
 * Get all jobs from BOTH pipelines as a flat array with stage and pipeline info.
 */
export function getJobs() {
  return [...getSalesPipelineJobs(), ...getJobsPipelineJobs()];
}

/**
 * Get jobs from the Sales Pipeline (pbf_pipeline).
 */
export function getSalesPipelineJobs() {
  const pipeline = load('pipeline', {});
  const jobs = [];
  for (const stage of Object.keys(pipeline)) {
    const cards = pipeline[stage];
    if (!Array.isArray(cards)) continue;
    for (const card of cards) {
      jobs.push({ ...card, stage, pipelineType: 'sales' });
    }
  }
  return jobs;
}

/**
 * Get jobs from the Jobs Pipeline (pbf_jobs_pipeline).
 */
export function getJobsPipelineJobs() {
  const pipeline = load('jobs_pipeline', {});
  const jobs = [];
  for (const stage of Object.keys(pipeline)) {
    const cards = pipeline[stage];
    if (!Array.isArray(cards)) continue;
    for (const card of cards) {
      jobs.push({ ...card, stage, pipelineType: 'jobs' });
    }
  }
  return jobs;
}

/**
 * Get jobs filtered by stage.
 */
export function getJobsByStage(stage) {
  return getJobs().filter(j => j.stage === stage);
}

/**
 * Get today's scheduled jobs.
 */
export function getTodaysJobs() {
  const today = new Date().toISOString().split('T')[0];
  return getJobs().filter(j => {
    if (!j.scheduledDate) return false;
    const d = typeof j.scheduledDate === 'string' ? j.scheduledDate.split('T')[0] : '';
    return d === today;
  });
}

/**
 * Get a job by its ID (searches both pipelines).
 */
export function getJobById(id) {
  return getJobs().find(j => j.id === id) || null;
}

/**
 * Determine which store key a job lives in.
 */
function getStoreKeyForJob(id) {
  // Check jobs_pipeline first
  const jobsPipeline = load('jobs_pipeline', {});
  for (const stage of Object.keys(jobsPipeline)) {
    const cards = jobsPipeline[stage];
    if (!Array.isArray(cards)) continue;
    if (cards.some(c => c.id === id)) return 'jobs_pipeline';
  }
  // Check sales pipeline
  const salesPipeline = load('pipeline', {});
  for (const stage of Object.keys(salesPipeline)) {
    const cards = salesPipeline[stage];
    if (!Array.isArray(cards)) continue;
    if (cards.some(c => c.id === id)) return 'pipeline';
  }
  return null;
}

/**
 * Update a job's properties.
 */
export function updateJob(id, updates) {
  const storeKey = getStoreKeyForJob(id);
  if (!storeKey) return null;
  const pipeline = load(storeKey, {});
  for (const stage of Object.keys(pipeline)) {
    const cards = pipeline[stage];
    if (!Array.isArray(cards)) continue;
    const idx = cards.findIndex(c => c.id === id);
    if (idx !== -1) {
      cards[idx] = { ...cards[idx], ...updates, updatedAt: new Date().toISOString() };
      store(storeKey, pipeline);
      return cards[idx];
    }
  }
  return null;
}

/**
 * Move a job to a new pipeline stage.
 */
export function moveJobToStage(id, newStage) {
  const storeKey = getStoreKeyForJob(id);
  if (!storeKey) return null;
  const pipeline = load(storeKey, {});
  let job = null;
  let oldStage = null;

  // Find and remove from current stage
  for (const stage of Object.keys(pipeline)) {
    const cards = pipeline[stage];
    if (!Array.isArray(cards)) continue;
    const idx = cards.findIndex(c => c.id === id);
    if (idx !== -1) {
      job = cards.splice(idx, 1)[0];
      oldStage = stage;
      break;
    }
  }

  if (!job) return null;

  // Add stage history entry
  if (!job.stageHistory) job.stageHistory = [];
  job.stageHistory.push({ from: oldStage, to: newStage, at: new Date().toISOString() });
  job.updatedAt = new Date().toISOString();

  // Determine if we need to move between pipelines
  const isNewStageInJobs = JOBS_STAGES.includes(newStage);
  const targetKey = isNewStageInJobs ? 'jobs_pipeline' : 'pipeline';

  if (targetKey === storeKey) {
    // Same pipeline
    if (!pipeline[newStage]) pipeline[newStage] = [];
    pipeline[newStage].push(job);
    store(storeKey, pipeline);
  } else {
    // Moving between pipelines
    store(storeKey, pipeline); // Save removal
    const targetPipeline = load(targetKey, {});
    if (!targetPipeline[newStage]) targetPipeline[newStage] = [];
    targetPipeline[newStage].push(job);
    store(targetKey, targetPipeline);
  }

  return { ...job, stage: newStage };
}

/**
 * Add a note to a job.
 */
export function addJobNote(id, noteText) {
  const storeKey = getStoreKeyForJob(id);
  if (!storeKey) return null;
  const pipeline = load(storeKey, {});
  for (const stage of Object.keys(pipeline)) {
    const cards = pipeline[stage];
    if (!Array.isArray(cards)) continue;
    const card = cards.find(c => c.id === id);
    if (card) {
      if (!card.notes) card.notes = [];
      card.notes.push({
        id: Date.now().toString(36),
        text: noteText,
        createdAt: new Date().toISOString(),
      });
      card.updatedAt = new Date().toISOString();
      store(storeKey, pipeline);
      return card;
    }
  }
  return null;
}

/**
 * Add a photo URL to a job.
 */
export function addJobPhoto(id, photoUrl) {
  const storeKey = getStoreKeyForJob(id);
  if (!storeKey) return null;
  const pipeline = load(storeKey, {});
  for (const stage of Object.keys(pipeline)) {
    const cards = pipeline[stage];
    if (!Array.isArray(cards)) continue;
    const card = cards.find(c => c.id === id);
    if (card) {
      if (!card.photos) card.photos = [];
      card.photos.push({
        url: photoUrl,
        addedAt: new Date().toISOString(),
      });
      card.updatedAt = new Date().toISOString();
      store(storeKey, pipeline);
      return card;
    }
  }
  return null;
}

/**
 * Get the customer object for a job.
 */
export function getJobCustomer(job) {
  if (!job || !job.customerId) return null;
  const customers = load('customers', []);
  return customers.find(c => c.id === job.customerId) || null;
}

/**
 * Get the list of jobs pipeline stages.
 */
export function getStages() {
  return JOBS_STAGES;
}

/**
 * Get the list of sales pipeline stages.
 */
export function getSalesStages() {
  return SALES_STAGES;
}

/**
 * Get the next stage in the appropriate pipeline.
 */
export function getNextStage(currentStage) {
  // Check jobs pipeline first
  let idx = JOBS_STAGES.indexOf(currentStage);
  if (idx !== -1) {
    return idx < JOBS_STAGES.length - 1 ? JOBS_STAGES[idx + 1] : null;
  }
  // Check sales pipeline
  idx = SALES_STAGES.indexOf(currentStage);
  if (idx !== -1) {
    // If at 'Booked', next stage is 'Accepted' (moves to jobs pipeline)
    if (currentStage === 'Booked') return 'Accepted';
    return idx < SALES_STAGES.length - 1 ? SALES_STAGES[idx + 1] : null;
  }
  return null;
}

/**
 * Get active jobs from both pipelines (for timer/camera dropdowns).
 */
export function getActiveJobs() {
  return getJobs().filter(j =>
    j.stage !== 'Paid & Closed' &&
    j.stage !== 'Completed' &&
    j.stage !== 'Proposal Rejected'
  );
}
